import type { ApiError } from '@/lib/api/errors';

/**
 * Did this request fail because the entry's hours are locked, and by what?
 *
 * The override is only ever the right remedy for a lock. Treating "the write
 * failed" as "the record is locked" tells a user their visit has been invoiced
 * when the truth was a dropped connection, an overlapping entry, or a missing
 * permission — and then offers them an override that fails again or fixes the
 * wrong thing.
 *
 * So read the refusal instead of counting it. The scheduler returns its lock
 * refusals as TIME_ENTRY_INVALID_STATE carrying the invoice or approval that
 * caused them. The same code is also used for "this time entry is already
 * closed", which carries neither — requiring one of the two ids is what keeps
 * that case out.
 */
export type TimeEntryLockKind = 'invoice' | 'payroll';

function responseData(err: unknown): unknown {
  const apiError = err as Partial<ApiError>;
  if (apiError?.data) return apiError.data;
  return (err as { response?: { data?: unknown } })?.response?.data;
}

export function timeEntryLockFromError(err: unknown): TimeEntryLockKind | null {
  if (!err) return null;
  const data = responseData(err) as
    | { code?: unknown; metadata?: { invoiceId?: unknown; approvalId?: unknown } }
    | undefined;
  if (data?.code !== 'TIME_ENTRY_INVALID_STATE') return null;
  if (typeof data.metadata?.invoiceId === 'string') return 'invoice';
  if (typeof data.metadata?.approvalId === 'string') return 'payroll';
  return null;
}

/** What to tell the user the lock is, in one sentence. */
export function describeTimeEntryLock(kind: TimeEntryLockKind): string {
  return kind === 'invoice'
    ? 'This work has already been invoiced.'
    : 'This entry sits in an approved pay period.';
}
